/**
 * ResetPassword — Phase 26
 *
 * Public page at /reset-password?token=... — entered via the reset link in
 * the password_reset email. Calls POST /api/auth/reset-password.
 * On success the OLD JWT becomes invalid (token_version bump) so we route
 * the user back to /login to authenticate with their new password.
 */
import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (pw.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (pw !== pw2) { setError("Passwords don't match."); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate("/login"), 1800);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Reset failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-10">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl shadow-sm p-7">
        <div className="text-sm font-bold text-slate-900 mb-1 tracking-tight">
          <span className="text-indigo-600">●</span> RunIdea.ai
        </div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight mt-3">Choose a new password</h1>
        <p className="text-sm text-slate-600 mt-1">After this, you'll be signed out of every device for security.</p>

        {!token ? (
          <div data-testid="reset-password-no-token" className="mt-5 flex items-start gap-2 p-3 rounded-md border border-rose-200 bg-rose-50">
            <AlertTriangle className="w-4 h-4 text-rose-700 mt-0.5" />
            <div className="text-sm text-rose-800">No reset token in the URL. Please use the link from your password-reset email.</div>
          </div>
        ) : done ? (
          <div data-testid="reset-password-success" className="mt-5 flex items-start gap-3 p-4 rounded-md bg-emerald-50 border border-emerald-200">
            <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5" />
            <div className="text-sm">
              <div className="font-semibold text-emerald-900">Password updated.</div>
              <div className="text-emerald-800 mt-1">Redirecting you to the login page…</div>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-5 space-y-4">
            <div>
              <Label htmlFor="pw" className="text-xs font-medium text-slate-700">New password</Label>
              <Input data-testid="reset-password-pw" id="pw" type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters" minLength={8} required />
            </div>
            <div>
              <Label htmlFor="pw2" className="text-xs font-medium text-slate-700">Confirm new password</Label>
              <Input data-testid="reset-password-pw2" id="pw2" type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} required />
            </div>
            {error && (
              <div data-testid="reset-password-error" className="flex items-start gap-2 p-3 rounded-md border border-rose-200 bg-rose-50">
                <AlertTriangle className="w-4 h-4 text-rose-700 mt-0.5" />
                <div className="text-sm text-rose-800">{String(error)}</div>
              </div>
            )}
            <Button data-testid="reset-password-submit" type="submit" disabled={submitting} className="w-full">
              {submitting ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : null}
              Update password
            </Button>
          </form>
        )}

        <p className="text-xs text-slate-500 mt-5 text-center">
          <Link to="/login" className="text-indigo-600 hover:text-indigo-700">← Back to login</Link>
        </p>
      </div>
    </div>
  );
}
